import React, { useEffect, useRef } from "react";

export default function Modal({
  open,
  title,
  message,
  onClose,
  type = "info",
  onConfirm,
  confirmText,
  confirmLoading = false,
  children,
}) {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    if (open) {
      previousActiveElement.current = document.activeElement;

      if (modalRef.current && document.activeElement === document.body) {
        modalRef.current.focus();
      }

      const handleEsc = (e) => {
        if (e.key === "Escape" && onClose) {
          onClose();
        }
      };
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";

      return () => {
        document.removeEventListener("keydown", handleEsc);
        document.body.style.overflow = "";

        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [open, onClose]);

  if (!open) return null;
  let color = "text-[#333333]";
  if (type === "error") color = "text-red-600";
  if (type === "success") color = "text-[#f26522]";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#333333]/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
      aria-describedby={message ? "modal-description" : undefined}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className="peer-home-page-card p-6 w-full max-w-sm relative focus:outline-none"
        tabIndex={-1}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-[#777777] hover:text-[#333333] text-xl focus:outline-none focus:ring-2 focus:ring-[#f26522] focus:ring-offset-2 rounded"
          aria-label="Close dialog"
        >
          &times;
        </button>
        {title && (
          <h2 id="modal-title" className="text-lg font-bold mb-2 text-center text-[#333333]">
            {title}
          </h2>
        )}
        {message && (
          <div id="modal-description" className={`text-center mb-2 ${color}`}>
            {message}
          </div>
        )}
        {children}
        {onConfirm && confirmText ? (
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="w-1/2 peer-home-btn-secondary py-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                if (confirmLoading || !onConfirm) return;
                await onConfirm();
              }}
              disabled={confirmLoading}
              className="w-1/2 peer-home-btn-primary py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {confirmLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Loading...
                </>
              ) : (
                confirmText
              )}
            </button>
          </div>
        ) : (
          <button onClick={onClose} className="mt-2 w-full peer-home-btn-primary py-2">
            OK
          </button>
        )}
      </div>
    </div>
  );
}
